// @section: lead-capture-modal
import { useState, useEffect, useRef } from 'react';
import { X, Download, CheckCircle, Loader2, Lock, Shield, Zap } from 'lucide-react';

interface LeadCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  nombre: string;
  correo: string;
  celular: string;
}

interface FormErrors {
  nombre?: string;
  correo?: string;
  celular?: string;
}

const EBOOK_URL = '#';   // ← Reemplaza con la URL real del PDF

function validate(data: FormData): FormErrors {
  const errors: FormErrors = {};
  if (!data.nombre.trim()) {
    errors.nombre = 'Este campo es obligatorio para enviarte el ebook.';
  } else if (data.nombre.trim().length < 3) {
    errors.nombre = 'Ingresa tu nombre completo (mínimo 3 caracteres).';
  }
  if (!data.correo.trim()) {
    errors.correo = 'Ingresa el correo donde te enviamos el ebook.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.correo)) {
    errors.correo = 'Revisa que el correo tenga el formato correcto (ej: nombre@empresa.com).';
  }
  if (!data.celular.trim()) {
    errors.celular = 'Ingresa tu número de WhatsApp para enviarte soporte.';
  } else if (!/^[\d\s\+\-\(\)]{7,20}$/.test(data.celular)) {
    errors.celular = 'Ingresa un número válido con código de país (ej: +57 300 000 0000).';
  }
  return errors;
}

export default function LeadCaptureModal({ isOpen, onClose }: LeadCaptureModalProps) {
  /* ── State ── */
  const [form, setForm] = useState<FormData>({ nombre: '', correo: '', celular: '' });
  const [touched, setTouched] = useState<Partial<Record<keyof FormData, boolean>>>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const nombreRef = useRef<HTMLInputElement>(null);

  /* ── Lifecycle ── */
  useEffect(() => {
    if (isOpen) {
      // block body scroll
      document.body.style.overflow = 'hidden';
      // autofocus first field after animation
      setTimeout(() => nombreRef.current?.focus(), 380);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  /* ── Handlers ── */
  function handleChange(field: keyof FormData, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
    // Clear error only after user starts re-typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }

  function handleBlur(field: keyof FormData) {
    setTouched(prev => ({ ...prev, [field]: true }));
    const fieldErrors = validate({ ...form });
    setErrors(prev => ({ ...prev, [field]: fieldErrors[field] }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const allTouched = { nombre: true, correo: true, celular: true };
    setTouched(allTouched);
    const fieldErrors = validate(form);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      // Focus first field with error
      const first = Object.keys(fieldErrors)[0] as keyof FormData;
      document.getElementById(`modal-${first}`)?.focus();
      return;
    }
    setLoading(true);
    // Simulate API call (0.8s)
    await new Promise(r => setTimeout(r, 800));
    setLoading(false);
    setSuccess(true);
  }

  function handleClose() {
    // Persist form data: do NOT reset if user didn't complete (UX recommendation #10)
    if (success) {
      setSuccess(false);
      setForm({ nombre: '', correo: '', celular: '' });
      setTouched({});
      setErrors({});
    }
    onClose();
  }

  function handleDownload() {
    window.open(EBOOK_URL, '_blank');
    handleClose();
  }

  if (!isOpen) return null;

  return (
    /* ── Overlay ── */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 animate-overlay-in"
      style={{ backgroundColor: 'rgba(0,0,0,0.82)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* ── Modal panel ── */}
      <div
        className="modal-sheet animate-modal-in relative w-full max-w-[480px] rounded-[20px] p-7 sm:p-8 shadow-2xl"
        style={{
          background: 'oklch(0.10 0.024 230)',
          border: '1px solid oklch(0.72 0.14 45 / 0.2)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px oklch(0.72 0.14 45 / 0.08)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-lg transition-colors hover:bg-white/5"
          aria-label="Cerrar"
          style={{ color: 'oklch(0.55 0.015 230)' }}
        >
          <X size={16} />
        </button>

        {/* ════════════════════════════════════════
            PANTALLA 1: FORMULARIO
        ════════════════════════════════════════ */}
        {!success && (
          <form onSubmit={handleSubmit} noValidate>
            {/* Header */}
            <div className="mb-6 pr-4">
              {/* Social proof pill */}
              <div className="flex items-center gap-2 mb-4">
                <span className="badge-cyan">📥 +1.200 agentes ya lo descargaron</span>
              </div>
              <h2
                id="modal-title"
                className="text-2xl font-bold mb-2"
                style={{ fontFamily: 'Cormorant Garamond, serif', color: 'oklch(0.97 0.005 230)', lineHeight: 1.2 }}
              >
                Descarga tu Ebook <span className="text-gold-gradient">Gratis</span>
              </h2>
              <p style={{ color: 'oklch(0.65 0.015 230)', fontSize: '14px', lineHeight: 1.6 }}>
                Completa tus datos y recibe acceso inmediato a los{' '}
                <strong style={{ color: 'oklch(0.82 0.14 45)' }}>100 Prompts Maestros.</strong>
                <br />
                <span style={{ fontSize: '12px' }}>📘 86 páginas · 100 prompts · 20 categorías</span>
              </p>
            </div>

            {/* Fields */}
            <div className="space-y-4 mb-5">
              {/* Nombre */}
              <div>
                <label
                  htmlFor="modal-nombre"
                  style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: 'oklch(0.82 0.01 230)' }}
                >
                  Tu nombre
                </label>
                <input
                  ref={nombreRef}
                  id="modal-nombre"
                  type="text"
                  autoComplete="name"
                  placeholder="Ej: Carlos Mendoza"
                  value={form.nombre}
                  onChange={e => handleChange('nombre', e.target.value)}
                  onBlur={() => handleBlur('nombre')}
                  className={`form-input${touched.nombre && errors.nombre ? ' error' : ''}`}
                  style={{ minHeight: '48px' }}
                  disabled={loading}
                />
                {touched.nombre && errors.nombre && (
                  <p style={{ color: 'oklch(0.65 0.20 25)', fontSize: '12px', marginTop: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    ⚠ {errors.nombre}
                  </p>
                )}
              </div>

              {/* Correo */}
              <div>
                <label
                  htmlFor="modal-correo"
                  style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: 'oklch(0.82 0.01 230)' }}
                >
                  Correo donde te enviamos el ebook
                </label>
                <input
                  id="modal-correo"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="nombre@tuagencia.com"
                  value={form.correo}
                  onChange={e => handleChange('correo', e.target.value)}
                  onBlur={() => handleBlur('correo')}
                  className={`form-input${touched.correo && errors.correo ? ' error' : ''}`}
                  style={{ minHeight: '48px' }}
                  disabled={loading}
                />
                {touched.correo && errors.correo && (
                  <p style={{ color: 'oklch(0.65 0.20 25)', fontSize: '12px', marginTop: '5px' }}>
                    ⚠ {errors.correo}
                  </p>
                )}
              </div>

              {/* Celular */}
              <div>
                <label
                  htmlFor="modal-celular"
                  style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: 'oklch(0.82 0.01 230)' }}
                >
                  WhatsApp <span style={{ fontWeight: 400, color: 'oklch(0.55 0.015 230)' }}>(para soporte y actualizaciones)</span>
                </label>
                <input
                  id="modal-celular"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="+57 300 000 0000"
                  value={form.celular}
                  onChange={e => handleChange('celular', e.target.value)}
                  onBlur={() => handleBlur('celular')}
                  className={`form-input${touched.celular && errors.celular ? ' error' : ''}`}
                  style={{ minHeight: '48px' }}
                  disabled={loading}
                />
                {touched.celular && errors.celular && (
                  <p style={{ color: 'oklch(0.65 0.20 25)', fontSize: '12px', marginTop: '5px' }}>
                    ⚠ {errors.celular}
                  </p>
                )}
              </div>
            </div>

            {/* CTA Button */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary-glow w-full flex items-center justify-center gap-2 py-4 px-6 rounded-xl text-sm font-black tracking-widest uppercase"
              style={{ color: 'oklch(0.10 0.02 250)', minHeight: '54px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.85 : 1 }}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Preparando tu descarga…
                </>
              ) : (
                <>
                  <Zap size={18} />
                  Descargar Ebook Ahora
                </>
              )}
            </button>

            {/* Trust badges */}
            <div className="mt-4 space-y-2">
              {[
                'No compartimos tu información con nadie.',
                'Descarga inmediata — sin esperas.',
                'Sin spam. Cancela cuando quieras.',
              ].map(text => (
                <div key={text} className="flex items-center gap-2" style={{ fontSize: '12px', color: 'oklch(0.65 0.015 230)' }}>
                  <CheckCircle size={13} style={{ color: 'oklch(0.68 0.18 145)', flexShrink: 0 }} />
                  {text}
                </div>
              ))}
            </div>

            {/* Security line */}
            <div className="mt-3 flex items-center justify-center gap-1" style={{ fontSize: '11px', color: 'oklch(0.45 0.012 230)' }}>
              <Lock size={10} />
              Formulario 100% seguro · Tus datos están protegidos
            </div>
          </form>
        )}

        {/* ════════════════════════════════════════
            PANTALLA 2: ÉXITO
        ════════════════════════════════════════ */}
        {success && (
          <div className="text-center py-2 animate-success-in">
            {/* Animated check */}
            <div className="flex justify-center mb-5">
              <div
                className="animate-pop-in flex items-center justify-center rounded-full w-20 h-20"
                style={{ background: 'oklch(0.68 0.18 145 / 0.15)', border: '2px solid oklch(0.68 0.18 145 / 0.4)' }}
              >
                <CheckCircle size={40} style={{ color: 'oklch(0.68 0.18 145)' }} />
              </div>
            </div>

            <h2
              className="text-2xl font-bold mb-2"
              style={{ fontFamily: 'Cormorant Garamond, serif', color: 'oklch(0.97 0.005 230)' }}
            >
              ¡Perfecto! Tu ebook está listo.
            </h2>
            <p style={{ color: 'oklch(0.65 0.015 230)', fontSize: '14px', lineHeight: 1.7, marginBottom: '24px' }}>
              Hemos enviado el enlace de descarga a{' '}
              <strong style={{ color: 'oklch(0.72 0.14 45)' }}>{form.correo}</strong>
              <br />
              Revisa tu bandeja de entrada (y la carpeta de spam si no llega en 2 minutos).
            </p>

            {/* Download CTA */}
            <button
              onClick={handleDownload}
              className="btn-primary-glow w-full flex items-center justify-center gap-2 py-4 px-6 rounded-xl text-sm font-black tracking-widest uppercase mb-5"
              style={{ color: 'oklch(0.10 0.02 250)', minHeight: '54px' }}
            >
              <Download size={18} />
              Descargar Ahora
            </button>

            {/* Divider */}
            <div className="gold-divider mb-4" />

            {/* Social micro-CTA */}
            <p style={{ fontSize: '13px', color: 'oklch(0.55 0.015 230)', marginBottom: '12px' }}>
              ¿Quieres más recursos para agentes de viajes?
            </p>
            <div className="flex gap-3 justify-center">
              <a
                href="https://wa.me/573001234567"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-4 py-2 rounded-lg text-xs font-semibold transition-colors"
                style={{ background: 'oklch(0.68 0.18 145 / 0.12)', border: '1px solid oklch(0.68 0.18 145 / 0.3)', color: 'oklch(0.75 0.18 145)' }}
              >
                💬 WhatsApp
              </a>
              <a
                href={`mailto:info@consultor-ia.co`}
                className="flex items-center gap-1 px-4 py-2 rounded-lg text-xs font-semibold transition-colors"
                style={{ background: 'oklch(0.72 0.14 45 / 0.10)', border: '1px solid oklch(0.72 0.14 45 / 0.25)', color: 'oklch(0.82 0.14 45)' }}
              >
                ✉ Email
              </a>
            </div>

            {/* Security badges */}
            <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
              {[
                { icon: <Shield size={12} />, text: 'Datos seguros' },
                { icon: <Lock size={12} />,   text: 'Sin spam' },
                { icon: <CheckCircle size={12} />, text: 'Acceso inmediato' },
              ].map(b => (
                <div key={b.text} className="flex items-center gap-1" style={{ fontSize: '11px', color: 'oklch(0.45 0.012 230)' }}>
                  {b.icon}{b.text}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
